import axiosInstance from '../../services/axiosInstance';
import { 
  Brief, 
  AdPlacementTemplate, 
  BriefProduct, 
  ProductSearchResult,
  BriefAdCopyVariant 
} from './types';

// =============================================
// Annonsmallar (AdPlacementTemplate)
// =============================================

export async function fetchTemplates(type?: 'print' | 'digital'): Promise<AdPlacementTemplate[]> {
  const params = type ? { type } : {};
  const { data } = await axiosInstance.get<AdPlacementTemplate[]>('/api/briefs/templates', { params });
  return data;
}

export async function fetchTemplate(id: string): Promise<AdPlacementTemplate> {
  const { data } = await axiosInstance.get<AdPlacementTemplate>(`/api/briefs/templates/${id}`);
  return data;
}

// =============================================
// Briefs (CRUD)
// =============================================

export interface FetchBriefsParams {
  status?: string;
  search?: string;
  limit?: number;
}

export async function fetchBriefs(params?: FetchBriefsParams): Promise<Brief[]> {
  const { data } = await axiosInstance.get<Brief[]>('/api/briefs', { params });
  return data;
}

export async function fetchBrief(id: string): Promise<Brief> {
  const { data } = await axiosInstance.get<Brief>(`/api/briefs/${id}`);
  return data;
}

export interface CreateBriefParams {
  type: 'print' | 'digital';
  title?: string;
  placementTemplateId?: string;
}

export async function createBrief(params: CreateBriefParams): Promise<Brief> {
  const { data } = await axiosInstance.post<Brief>('/api/briefs', params);
  return data;
}

export async function updateBrief(id: string, updates: Partial<Brief>): Promise<Brief> {
  const { data } = await axiosInstance.put<Brief>(`/api/briefs/${id}`, updates);
  return data;
}

export async function deleteBrief(id: string): Promise<void> {
  await axiosInstance.delete(`/api/briefs/${id}`);
}

// =============================================
// Produkter
// =============================================

export async function searchProducts(query: string): Promise<ProductSearchResult[]> {
  const { data } = await axiosInstance.get<ProductSearchResult[]>('/api/briefs/products/search', {
    params: { q: query, limit: 20 }
  });
  return data;
}

export interface AddProductParams {
  assetId: string;
  role?: 'hero' | 'support';
  price?: {
    amount: number;
    currency?: string;
    type?: 'campaign' | 'regular';
    originalPrice?: number;
    validFrom?: string;
    validTo?: string;
  };
}

export async function addProduct(briefId: string, params: AddProductParams): Promise<BriefProduct[]> {
  const { data } = await axiosInstance.post<BriefProduct[]>(`/api/briefs/${briefId}/products`, params);
  return data;
}

export async function removeProduct(briefId: string, assetId: string): Promise<BriefProduct[]> {
  const { data } = await axiosInstance.delete<BriefProduct[]>(`/api/briefs/${briefId}/products/${assetId}`);
  return data;
}

// =============================================
// Bilagor
// =============================================

export async function uploadAttachment(briefId: string, file: File): Promise<any[]> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axiosInstance.post(`/api/briefs/${briefId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return data;
}

export async function deleteAttachment(briefId: string, attachmentId: string): Promise<any[]> {
  const { data } = await axiosInstance.delete(`/api/briefs/${briefId}/attachments/${attachmentId}`);
  return data;
}

// =============================================
// AI-generering
// =============================================

export interface AiGenerateResult {
  briefText: string;
  strategyText?: string;
  strategySuggestionText?: string;
  adCopyVariants: BriefAdCopyVariant[];
  missingInfoQuestions: string[];
}

export async function checkAiStatus(): Promise<{ available: boolean }> {
  const { data } = await axiosInstance.get<{ available: boolean }>('/api/briefs/ai/status');
  return data;
}

export async function generateBriefAi(briefId: string): Promise<AiGenerateResult> {
  const { data } = await axiosInstance.post<AiGenerateResult>(`/api/briefs/${briefId}/ai/generate`);
  return data;
}

// =============================================
// ClickUp-integration
// =============================================

export interface SendToClickupResult {
  success: boolean;
  taskId: string;
  taskUrl: string;
  message: string;
}

export interface SendToClickupPayload {
  briefText?: string;
  strategyText?: string;
  includeStrategyInClickup?: boolean;
}

export async function sendToClickup(briefId: string, payload: SendToClickupPayload): Promise<SendToClickupResult> {
  const { data } = await axiosInstance.post<SendToClickupResult>(`/api/briefs/${briefId}/clickup/send`, payload);
  return data;
}
